// This is a modified version of `app-playbooks-trigger` component and will eventually replace it when the feature is generally available.

import { Component, Inject, OnInit, signal, WritableSignal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { serializeTriggerConfiguration } from 'src/app/common/playbookUtils';
import { PlaybookTriggerConfigBase } from 'src/app/models/api/playbooks/playbook.model';
import {
  PlaybooksTriggerConfigInput,
  PlaybooksTriggerInput,
} from 'src/app/models/componentInputs/playbooksTriggerInput.interface';
import { PlaybooksTriggerOutput } from 'src/app/models/popups/outputs/playbooksTriggerOutput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { PlaybookTemplatesService } from 'src/app/services/templates/playbook/playbook-templates.service';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { DetectionRulesHttpService } from 'src/app/services/httpServices/governance/detectionRulesHttpService/detection-rules-http.service';
import { ViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import {
  TriggerCoverage,
  TriggerType,
  getTriggerTypesFromCombination,
  getCombinationFromTriggerTypes,
} from './triggerMapping';

@Component({
  selector: 'app-new-playbooks-trigger',
  templateUrl: './new-playbooks-trigger.component.html',
  styleUrls: ['./new-playbooks-trigger.component.scss'],
})
export class NewPlaybooksTriggerComponent implements OnInit {
  public selectedTriggerType: TriggerType;
  public selectedTriggerCoverage: TriggerCoverage;

  public supportedTriggerTypes = TriggerType;
  public supportedTriggerCoverages = TriggerCoverage;
  public triggerTypeOptions: TriggerType[];
  public triggerCoverageOptions = Object.values(TriggerCoverage);

  public fg = new UntypedFormGroup({});

  public triggerConfig?: PlaybookTriggerConfigBase;
  public triggerFormInput: PlaybooksTriggerConfigInput;
  public isEdit = false;
  public isConfigurationValid: WritableSignal<boolean> = signal<boolean>(true);

  public resourceTypeOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public detectionRules: WritableSignal<ViolationRule[]> = signal<ViolationRule[]>([]);

  constructor(
    public dialogRef: MatDialogRef<NewPlaybooksTriggerComponent>,
    @Inject(MAT_DIALOG_DATA) public triggerInput: PlaybooksTriggerInput,
    private _playbookTemplatesService: PlaybookTemplatesService,
    private _beautifyPipe: BeautifyDisplayStringPipe,
    private _detectionRulesService: DetectionRulesHttpService,
    private _featureFlagsService: FeatureFlagsService
  ) {
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeWindow();
    });

    if (triggerInput.trigger) {
      const combination = getCombinationFromTriggerTypes(triggerInput.trigger.triggerType);
      this.selectedTriggerType = combination.type;
      this.selectedTriggerCoverage = combination.coverage;

      if (this.selectedTriggerType === TriggerType.findings || this.selectedTriggerType === TriggerType.issues) {
        this.selectedTriggerCoverage = TriggerCoverage.new;
      }

      this.isEdit = true;
    } else {
      this.selectedTriggerType = TriggerType.violations;
      this.selectedTriggerCoverage = TriggerCoverage.all;
    }

    this.triggerFormInput = {
      integration: triggerInput.integration,
      triggerType: getTriggerTypesFromCombination(this.selectedTriggerCoverage, this.selectedTriggerType),
      triggerConfig: triggerInput.trigger?.configuration,
    };
  }

  public ngOnInit() {
    this.resourceTypeOptions$ = this._playbookTemplatesService.getResourceTypeOptions$();
    this._checkDetectionRules();
  }

  private _initializeTriggerTypeOptions() {
    let triggerTypes = Object.values(TriggerType);

    // Filter out issues trigger if feature flag is disabled
    const isIssuePlaybooksEnabled =
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.IssueTriggeredPlaybooks);
    if (!isIssuePlaybooksEnabled) {
      triggerTypes = triggerTypes.filter((type) => type !== TriggerType.issues);
    }

    this.triggerTypeOptions = triggerTypes;
  }

  private _checkDetectionRules() {
    const integrationId = this.triggerInput.integration?.id;
    if (integrationId) {
      this._detectionRulesService.getDetectionRules(integrationId).subscribe({
        next: (rules) => {
          const availableRules = rules.filter((rule) => rule.status === 'on');
          this.detectionRules.set(availableRules);
          this._initializeTriggerTypeOptions();
        },
        error: (error) => {
          console.error('Failed to load detection rules:', error);
          this.detectionRules.set([]);
          this._initializeTriggerTypeOptions();
        },
      });
    } else {
      this.detectionRules.set([]);
      this._initializeTriggerTypeOptions();
    }
  }

  public typeSelected(type: TriggerType) {
    if (type == this.selectedTriggerType) {
      return;
    }

    this._resetTriggerOnChange();
    this.selectedTriggerType = type;

    if (type === TriggerType.findings || type === TriggerType.issues) {
      this.selectedTriggerCoverage = TriggerCoverage.new;
    }

    this.fg = new UntypedFormGroup({});
    this.triggerFormInput = {
      integration: this.triggerInput.integration,
      triggerType: getTriggerTypesFromCombination(this.selectedTriggerCoverage, this.selectedTriggerType),
      triggerConfig: this.triggerConfig,
    } as PlaybooksTriggerConfigInput;
  }

  public coverageSelected(coverage: TriggerCoverage) {
    this.selectedTriggerCoverage = coverage;
  }

  public closeWindow() {
    this.dialogRef.close({} as PlaybooksTriggerOutput);
  }

  public createOrUpdate() {
    if (this.isConfigurationValid()) {
      const configuration = serializeTriggerConfiguration(this.fg.value);

      this.dialogRef.close({
        trigger: {
          triggerType: getTriggerTypesFromCombination(this.selectedTriggerCoverage, this.selectedTriggerType),
          configuration: configuration as PlaybookTriggerConfigBase,
        },
      } as PlaybooksTriggerOutput);
    }
  }

  public setFormValidation(value: boolean) {
    this.isConfigurationValid.set(value);
  }

  private _resetTriggerOnChange() {
    this.triggerConfig = undefined;
  }

  public getTriggerTypeDisplayName(triggerType: TriggerType): string {
    if (triggerType === TriggerType.findings) {
      return 'Runtime Findings';
    }
    if (triggerType === TriggerType.issues) {
      return 'Issues';
    }
    return this._beautifyPipe.transform(triggerType);
  }
}
